import { Component, OnInit, Input, Output, EventEmitter, OnChanges } from '@angular/core';
import { Location } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup, FormArray, FormControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { AlertService } from '@app/_services';
import { ApiResponse } from '@app/_models';
import { LazyLoadEvent, MessageService, ConfirmationService } from 'primeng/api';
import { RateBucketDetailService } from '.';
import { take } from 'rxjs/operators';
import { RateBucketDetail } from './rate-bucket-detail';
import { MatDialog, MatDialogConfig, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { RateBucketDetailFormComponent } from './rate-bucket-detail-form/rate-bucket-detail-form.component';
import { RateBucketService } from '../rate-bucket/rate-bucket.service';
import { RateBulkEditComponent } from '../rate-bulk-edit/rate-bulk-edit.component';


@Component({
    selector: 'app-rate-bucket-detail',
    templateUrl: './rate-bucket-detail.component.html',
    styleUrls: ['./rate-bucket-detail.component.css'],
    providers: [MessageService, ConfirmationService, { provide: MAT_DIALOG_DATA, useValue: {} }],
    // make column heading sticky
    styles: [`
        :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
            position: -webkit-sticky;
            position: sticky;
            top: 0px;
        }

        @media screen and (max-width: 64em) {
            :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
                top: 99px;
            }
        }
`],
    standalone: false
})
export class RateBucketDetailComponent implements OnInit
{

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private rateBucketDetailService: RateBucketDetailService,
    private rateBucketService: RateBucketService,
    private alertService: AlertService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dialog: MatDialog,
    private location: Location,
    private formBuilder: UntypedFormBuilder) { }


  // structure
  subscription: Subscription;
  routerLocation = this.location;

  // data
  apiResponse: ApiResponse;
  rateBucketDetails: any;


  // p-table
  first = 0;
  rows = 50;
  datasource;
  recordTotal: number;
  lazyLoadEvent: LazyLoadEvent;
  loading = true;
  multiSortMeta = [];

  // forms
  addForm: UntypedFormGroup;
  editForm: UntypedFormGroup;
  formLoading = false;
  formLabels = [];
  formReadOnly = false;
  resetFilters: boolean;

  columns: any[] = [
    { field: 'rateBucketName', header: 'Bucket Name', width: 'auto' },
    { field: 'name', header: 'Name', width: 'auto' },
    { field: 'startDate', header: 'Start', width: 'auto' },
    { field: 'endDate', header: 'End', width: 'auto' },
    { field: 'listPrice', header: 'List Price', width: 'auto' },
    { field: 'listPriceAdjustmentAmount', header: 'Disc Price', width: 'auto' },
    { field: 'listPriceAdjustmentPercent', header: 'Disc Percent', width: 'auto' },
    { field: 'minimumPriceSetByManager', header: 'Mgr Min $', width: 'auto' },
    { field: 'lastUpdate', header: 'Updated', width: 'auto' },
  ];


  validationMessages = {
    'name': [
      { type: 'required', message: 'Required' }
    ],
    'description': [
      { type: 'required', message: 'Required' }
    ],

  }

  ngOnInit(): void
  {
    try
    {
      this.loadRateBucketDetails();

      // default sort of list
      this.multiSortMeta = [];
      this.multiSortMeta.push({ field: 'createDate', order: 0 });
    }
    catch (error)
    {
      console.log('Error: ' + error);
    }
  }

  /*********
   * Cell Edit Callbacks (if using cell edit feature).
   * Remove editMode="row" in p-table
   * Remove <tr [pEditableRow]="rateBucketDetail"> and just have plain <tr>
   * Add <td [pEditableColumn]="rateBucketDetail" [pEditableColumnField]="'name'"> for each cell <td>
   * Update <ng-template pTemplate="body" let-rateBucketDetail>
   *
   */
  onCellEditInit(event): void { }
  onCellEditComplete(event): void { this.formUpdate(event.data) }
  onCellEditCancel(): void { this.messageService.add({ severity: 'info', summary: 'Update Canceled', detail: 'No changes were saved' }); }



  /*********
   * Row Edit Callbacks (if using row edit feature)
   * Add editMode="row" in p-table
   * Add <tr [pEditableRow]="rateBucketDetail"> for the <tr>
   * Remove <td [pEditableColumn]="rateBucketDetail" [pEditableColumnField]="'name'"> for each cell <td>
   * <ng-template pTemplate="body" let-rateBucketDetail let-editing="editing" let-ri="rowIndex">
   */
  onRowEditInit(rateBucketDetail: RateBucketDetail): void { }
  onRowEditSave(rateBucketDetail: RateBucketDetail): void { }
  onRowEditCancel(): void { this.messageService.add({ severity: 'info', summary: 'Update Canceled', detail: 'No changes were saved' }); }



  /*********
   * Delete Button
   *
   */
  onRowDelete(event)
  {
    console.log('onRowDelete: ' + JSON.stringify(event));
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete Id# ' + event.rateBucketDetailId + '?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () =>
      {
        this.formDelete(event);
      },
      reject: () =>
      {
        this.messageService.add({ severity: 'info', summary: 'Canceled', detail: 'Delete canceled.', life: 3000 });
      }
    });
  }


  /*********
   * Load All data (not lazy load)
   *
   */
  loadRateBucketDetails()
  {
    try
    {
      this.formLoading = true;

      // use the .pipe(take(1)) to prevent double firing...
      this.rateBucketDetailService.findAll().pipe(take(1))
        .subscribe(
          res =>
          {

            this.alertService.success(res, true);
            this.apiResponse = res;
            this.rateBucketDetails = res.data.rateBucketDetails;

            //  console.log('Rate-Bucket-Load SUCCESS: ' + this.rateBucketDetails.length);
          },
          err =>
          {
            console.log('Rate-Bucket-Load FAIL: ' + JSON.stringify(err));
            this.messageService.add({ severity: 'error', summary: err.message, life: 3000 });
          },
          () => { this.formLoading = false; });

    } catch (error)
    {
      this.alertService.error(error);
    }
  }

  // handled in rate-bucket-form
  formAdd() { }


  // used for row editing
  formUpdate(rateBucketDetail: RateBucketDetail)
  {
    try
    {
      console.log('Rate-Bucket-Update: ' + JSON.stringify(rateBucketDetail));
      // this.formLoading = true;
      this.rateBucketDetailService.update(rateBucketDetail)
        .subscribe(
          res =>
          {
            console.log('Rate-Bucket-Update SUCCESS: ' + JSON.stringify(res));
            this.rateBucketDetails.map((rateBucketDetail, i) =>
            {
              if (rateBucketDetail.rateBucketDetailId === res.data.rateBucketDetail.rateBucketDetailId)
              {
                this.rateBucketDetails[i] = res.data.rateBucketDetail;
              }
            });
            // this.alertService.success(res, true);
            this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
          },
          err =>
          {
            console.log('Rate-Bucket-Update FAIL: ' + JSON.stringify(err));
            this.messageService.add({ severity: 'error', summary: err.message, life: 3000 });
          },
          () => { this.formLoading = false; });
    } catch (error)
    {
      console.log('Form Invalid: ' + JSON.stringify(error));
      this.alertService.error(error);
    }
  }

  // used for row editing
  formDelete(rateBucketDetail)
  {
    try
    {
      // stop here if form is invalid
      console.log('Delete Submitted: ' + JSON.stringify(rateBucketDetail));

      this.formLoading = true;
      this.rateBucketDetailService.delete(rateBucketDetail.rateBucketDetailId)
        .subscribe(
          res =>
          {
            console.log('Rate-Bucket-Delete SUCCESS: ' + JSON.stringify(res));
            this.rateBucketDetails.splice(this.rateBucketDetails.indexOf(rateBucketDetail), 1);
            this.recordTotal = this.rateBucketDetails.length;
            this.messageService.add({ severity: 'success', summary: 'Successful', detail: 'RateBucketDetail ' + rateBucketDetail.rateBucketDetailId + ' Deleted', life: 3000 });
          },
          err =>
          {
            console.log('Rate-Bucket-Delete  FAIL: ' + JSON.stringify(err));
            this.messageService.add({ severity: 'error', summary: 'Delete Failed', detail: 'RateBucketDetail ' + rateBucketDetail.rateBucketDetailId + ' could not be deleted.  Be sure there are no assigned properties to this bucket.', life: 3000 });
          },
          () => { this.formLoading = false; });
    } catch (error)
    {
      console.log('Form Invalid: ' + JSON.stringify(error));
      this.alertService.error(error);
    }
  }

  /*********
       * Add New Button
       *
       */
  onOpenFormAdd(rateBucketDetail: RateBucketDetail)
  {

    const dialogConfig = new MatDialogConfig();

    // settings
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = true;

    // pass data, ie rateBuicket: this.shortTermRentalId,
    // we pass the rateBucketDetail of the previous daterange if adding as an easy way to copy
    dialogConfig.data = {
      title: 'Add RateBucketDetail',
      rateBucketDetail: rateBucketDetail,
      formAction: 'add'
    };

    // open the popup and get a reference to it called dialogRef
    const dialogRef = this.dialog.open(RateBucketDetailFormComponent, dialogConfig);

    // to receive the added element, subscribe to the eventEmitter we created 'doAdd' in the element to handle adding
    dialogRef.componentInstance.doAdd.subscribe(
      data =>
      {
        console.log('Dialog output:', data);

        // when we doAdd in the element, call this parent and update the list so it shows the added element to the database
        this.addRateBucketDetailCB(data);

        // close the dialogBox
        dialogRef.close();
      });
  }

  bulkEdit()
  {
    const dialogConfig = new MatDialogConfig();

    // settings
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = true;

    // pass data, ie rateBuicket: this.shortTermRentalId,
    // we pass the rateBucketDetail of the previous daterange if adding as an easy way to copy
    dialogConfig.data = {
      title: 'Bulk Edit',
      formAction: 'bulkEdit'
    };

    // open the popup and get a reference to it called dialogRef
    const dialogRef = this.dialog.open(RateBulkEditComponent, dialogConfig);

    // to receive the added element, subscribe to the eventEmitter we created 'doAdd' in the element to handle adding
    dialogRef.componentInstance.doBulkEditCB.subscribe(
      data =>
      {
        // when we doAdd in the element, call this parent and update the list so it shows the added element to the database
        this.loadRateBucketDetails();
        this.messageService.add({ severity: 'success', summary: 'Success', detail: data, life: 5000 });

        // close the dialogBox
        dialogRef.close();
      });
  }


  /*********
      * Edit Button
      *
      */
  onOpenFormEdit(rateBucketDetail: RateBucketDetail)
  {

    const dialogConfig = new MatDialogConfig();

    // settings
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = true;

    // pass data, ie rateBuicket: this.shortTermRentalId,
    dialogConfig.data = {
      rateBucketDetail: rateBucketDetail,
      title: 'Edit RateBucketDetail',
      formAction: 'edit'
    };

    // open the popup and get a reference to it called dialogRef
    const dialogRef = this.dialog.open(RateBucketDetailFormComponent, dialogConfig);

    // to receive the added element, subscribe to the eventEmitter we created 'doUpdate' in the element to handle updating the list
    dialogRef.componentInstance.doUpdate.subscribe(
      data =>
      {
        console.log('Dialog output:', data);

        // when we doAdd in the element, call this parent and update the list so it shows the added element to the database
        this.updateRateBucketDetailCB(data);

        // close the dialogBox
        dialogRef.close();
      });

    dialogRef.componentInstance.doDelete.subscribe(
      data =>
      {
        console.log('Dialog output:', data);

        // when we doAdd in the element, call this parent and update the list so it shows the added element to the database
        this.deleteRateBucketDetailCB(data);

        // close the dialogBox
        dialogRef.close();
      });
  }




  /**
	 *
	 * Dialog from Form Callbacks
	 * To edit the list show add/edit/delete in real time from the popup form
	 *
	 */

  addRateBucketDetailCB(rateBucketDetail: RateBucketDetail)
  {
    console.log('Adding RateBucketDetail: ' + JSON.stringify(rateBucketDetail));
    // if we don't have a rateBucketDetails array yet...
    if (!this.rateBucketDetails)
    {
      this.rateBucketDetails = [];
    }
    // unshift adds to the front of the array (), push adds to the back
    this.rateBucketDetails.unshift(rateBucketDetail);

    // no idea what this is but it is required to update the array
    this.rateBucketDetails = [...this.rateBucketDetails];

    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'RateBucketDetail ' + rateBucketDetail.rateBucketDetailId + ' Added', life: 3000 });
  }


  updateRateBucketDetailCB(rateBucketDetail: RateBucketDetail)
  {
    console.log('Updating RateBucketDetail: ' + JSON.stringify(rateBucketDetail));
    let itemIndex = this.rateBucketDetails.findIndex(item => item.rateBucketDetailId === rateBucketDetail.rateBucketDetailId);
    this.rateBucketDetails[itemIndex] = rateBucketDetail;

    // no idea what this is but it is required to update the array
    this.rateBucketDetails = [...this.rateBucketDetails];

    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'RateBucketDetail ' + rateBucketDetail.rateBucketDetailId + ' Updated', life: 3000 });
  }

  deleteRateBucketDetailCB(rateBucketDetail: RateBucketDetail)
  {
    console.log('Removing RateBucketDetail: ' + rateBucketDetail);
    this.rateBucketDetails.splice(this.rateBucketDetails.indexOf(rateBucketDetail), 1);
    this.recordTotal = this.rateBucketDetails.length;

    // no idea what this is but it is required to update the array
    this.rateBucketDetails = [...this.rateBucketDetails];

    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'RateBucketDetail ' + rateBucketDetail.rateBucketDetailId + ' Deleted', life: 3000 });
  }


  /**********
   *
   * PAGINATOR FUNCTIONS
   */
  next() { this.first = this.first + this.rows; }
  prev() { this.first = this.first - this.rows; }
  reset() { this.first = 0; }
  isLastPage(): boolean { return this.rateBucketDetails ? this.first === (this.rateBucketDetails.length - this.rows) : true; }
  isFirstPage(): boolean { return this.rateBucketDetails ? this.first === 0 : true; }

  onResetFilter()
  {
    // this will return the list to full
    this.rateBucketDetails = this.apiResponse.data.rateBucketDetails;
    this.resetFilters = true;
  }




  /**********
     *
     * STR Autocomplete - if a str is selected, find which ratebucket it uses
     */
  filterByShortTermRental(shortTermRental: any)
  {
    console.log('filterByShortTermRental: ' + JSON.stringify(shortTermRental));
    try
    {
      this.formLoading = true;

      this.rateBucketDetailService.findByShortTermRentalId(shortTermRental.shortTermRentalId)
        .subscribe(
          res =>
          {
            // console.log('Rate-Bucket-Find-By STR SUCCESS: ' + JSON.stringify(res));
            // filter our rateBucketDetails

            if (!res.data.rates[0])
            {
              this.messageService.add({ severity: 'error', summary: 'No Rates Configured For ' + shortTermRental.propertyCode, life: 3000 });
            }
            console.log('dodo: ' + res.data.rates[0].rateBucketId);


            var filtered = this.rateBucketDetails.filter(function (a)
            {
              console.log(a.rateBucketId + '=' + res.data.rates[0].rateBucketId);
              return a.rateBucketId === res.data.rates[0].rateBucketId;
            });
            console.log('filter results: ' + JSON.stringify(filtered)
              + filtered.length);

            this.rateBucketDetails = filtered;

          },
          err =>
          {
            console.log('Rate-Bucket-Load FAIL: ' + JSON.stringify(err));
            this.messageService.add({ severity: 'error', summary: err.message, life: 3000 });
          },
          () => { this.formLoading = false; });

    } catch (error)
    {
      this.alertService.error(error);
    }
  }

}

